<script setup lang="ts">
/**
 * VOE Call Panel
 *
 * Replaces PhoneWidget content during VOE calls.
 * Shows: intro script, inline verification checklist (always visible),
 * call outcome, notes, and call controls.
 */
import { useVOECall } from '../composables/useVOECall'
import { usePhoneStore } from '../stores/phone'
import { computed } from 'vue'
import SsnReveal from './SsnReveal.vue'

const voe = useVOECall()
const phone = usePhoneStore()

const formattedDuration = computed(() => {
  const m = Math.floor(phone.callDuration / 60)
  const s = phone.callDuration % 60
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
})

const deskPhoneFormattedDuration = computed(() => {
  const m = Math.floor(voe.deskPhoneDuration.value / 60)
  const s = voe.deskPhoneDuration.value % 60
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
})

const outcomeOptions = [
  { label: 'Validated', value: 'validated', icon: 'pi pi-check-circle' },
  { label: 'Spoke with someone', value: 'spoke_with', icon: 'pi pi-user' },
  { label: 'Got new contact', value: 'got_referral', icon: 'pi pi-directions' },
  { label: 'Didn\'t work here', value: 'did_not_work_here', icon: 'pi pi-user-minus' },
  { label: 'Left voicemail', value: 'voicemail', icon: 'pi pi-volume-up' },
  { label: 'No answer', value: 'no_answer', icon: 'pi pi-phone' },
  { label: 'Wrong number', value: 'wrong_number', icon: 'pi pi-exclamation-circle' },
  { label: 'Disconnected', value: 'disconnected', icon: 'pi pi-times-circle' },
]

/** Format a date string (YYYY-MM-DD) for display */
function formatDate(d: string | null): string {
  if (!d) return 'Unknown'
  const date = new Date(d + 'T00:00:00')
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}
</script>

<template>
  <div class="flex flex-col h-full max-h-[calc(100vh-100px)]">
    <!-- Header — ClaimShield purple -->
    <div class="bg-primary-800 px-4 py-3 text-white shrink-0">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <div
            :class="[
              'h-2.5 w-2.5 rounded-full',
              phone.isOnCall || voe.deskPhoneStatus.value === 'in-progress'
                ? 'bg-green-400 animate-pulse'
                : voe.deskPhoneStatus.value === 'ringing'
                  ? 'bg-amber-400 animate-pulse'
                  : 'bg-primary-400'
            ]"
          />
          <span class="text-sm font-semibold">VOE Call</span>
          <span v-if="phone.isOnCall" class="text-xs font-mono text-green-300">
            {{ formattedDuration }}
          </span>
          <span v-else-if="voe.deskPhoneStatus.value === 'in-progress'" class="text-xs font-mono text-green-300">
            {{ deskPhoneFormattedDuration }}
          </span>
          <span v-else-if="voe.deskPhoneStatus.value === 'ringing'" class="text-xs font-mono text-amber-300 animate-pulse">
            Ringing...
          </span>
        </div>
        <button
          class="rounded-lg p-1 hover:bg-primary-700 transition-colors"
          @click="phone.isExpanded = false"
          v-tooltip.top="'Minimize'"
        >
          <i class="pi pi-minus text-sm" />
        </button>
      </div>
      <div class="mt-1 flex items-center gap-2 text-xs text-primary-200">
        <span class="truncate font-medium text-white">{{ voe.context.value?.employer.name }}</span>
        <span v-if="voe.formattedPhone.value">· {{ voe.formattedPhone.value }}</span>
      </div>
      <div class="mt-0.5 text-xs text-primary-300">
        {{ voe.attemptLabel.value }}
      </div>
    </div>

    <!-- Scrollable content -->
    <div class="flex-1 overflow-y-auto">
      <!-- Driver identity (last-4 SSN + DOB) — what previous employers
           commonly ask for before releasing employment history. -->
      <div
        v-if="voe.context.value?.record"
        class="px-4 py-2 border-b border-surface-200 bg-amber-50"
      >
        <div class="text-[10px] font-semibold text-amber-800 uppercase tracking-wider mb-1">
          Driver identity (if asked)
        </div>
        <div class="grid grid-cols-2 gap-3">
          <div>
            <div class="text-[10px] text-surface-500 uppercase">SSN</div>
            <div class="font-mono text-sm font-semibold text-surface-900">
              <SsnReveal
                :record-id="voe.context.value.record.id"
                :last4="voe.context.value.record.driver_ssn_last4"
              />
            </div>
          </div>
          <div>
            <div class="text-[10px] text-surface-500 uppercase">Date of birth</div>
            <div class="text-sm font-semibold text-surface-900">
              {{ formatDate(voe.context.value.record.driver_dob) }}
            </div>
          </div>
        </div>
      </div>

      <!-- Intro Script -->
      <div class="border-b border-surface-200">
        <button
          class="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-primary-700 uppercase tracking-wider hover:bg-primary-50 transition-colors"
          @click="voe.scriptCollapsed.value = !voe.scriptCollapsed.value"
        >
          <span>Intro Script</span>
          <i :class="voe.scriptCollapsed.value ? 'pi pi-chevron-down' : 'pi pi-chevron-up'" class="text-[10px]" />
        </button>
        <div
          v-show="!voe.scriptCollapsed.value"
          class="px-4 pb-3 text-xs text-surface-600 leading-relaxed italic"
        >
          "{{ voe.callScript.value }}"
        </div>
      </div>

      <!-- Call Outcome (moved above verification) -->
      <div class="px-4 py-3 border-b border-surface-200">
        <div class="text-xs font-semibold text-primary-700 uppercase tracking-wider mb-2">
          Call Outcome
        </div>
        <div class="grid grid-cols-2 gap-1.5">
          <button
            v-for="opt in outcomeOptions"
            :key="opt.value"
            :class="[
              'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors border',
              voe.outcome.value === opt.value
                ? opt.value === 'validated' ? 'border-green-500 bg-green-50 text-green-700' : 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-surface-200 text-surface-600 hover:bg-surface-50'
            ]"
            @click="voe.outcome.value = opt.value as any"
          >
            <i :class="opt.icon" class="text-[10px]" />
            {{ opt.label }}
          </button>
        </div>
      </div>

      <!-- Got New Contact (update employer contact info, does NOT count as attempt) -->
      <div
        v-if="voe.outcome.value === 'got_referral'"
        class="px-4 py-3 border-b border-surface-200 space-y-2"
      >
        <div class="text-xs font-semibold text-primary-700 uppercase tracking-wider">
          New Contact Info
        </div>
        <div class="text-[10px] text-amber-600 italic">This will update the employer's contact info. Does not count as an attempt.</div>
        <div class="grid grid-cols-2 gap-2">
          <div>
            <label class="block text-xs text-surface-500 mb-0.5">Contact Name</label>
            <input
              v-model="voe.referralContact.value.name"
              class="w-full rounded-lg border border-surface-300 px-2.5 py-1.5 text-xs focus:border-primary-500 focus:ring-1 focus:ring-primary-100 outline-none"
              placeholder="Name..."
            />
          </div>
          <div>
            <label class="block text-xs text-surface-500 mb-0.5">Title</label>
            <input
              v-model="voe.referralContact.value.title"
              class="w-full rounded-lg border border-surface-300 px-2.5 py-1.5 text-xs focus:border-primary-500 focus:ring-1 focus:ring-primary-100 outline-none"
              placeholder="Title..."
            />
          </div>
        </div>
        <div class="grid grid-cols-2 gap-2">
          <div>
            <label class="block text-xs text-surface-500 mb-0.5">Phone</label>
            <input
              v-model="voe.referralContact.value.phone"
              type="tel"
              class="w-full rounded-lg border border-surface-300 px-2.5 py-1.5 text-xs focus:border-primary-500 focus:ring-1 focus:ring-primary-100 outline-none"
              placeholder="(555) 123-4567"
            />
          </div>
          <div>
            <label class="block text-xs text-surface-500 mb-0.5">Email</label>
            <input
              v-model="voe.referralContact.value.email"
              type="email"
              class="w-full rounded-lg border border-surface-300 px-2.5 py-1.5 text-xs focus:border-primary-500 focus:ring-1 focus:ring-primary-100 outline-none"
              placeholder="hr@company.com"
            />
          </div>
        </div>
        <textarea
          v-model="voe.notes.value"
          placeholder="Notes about the referral..."
          class="w-full rounded-lg border border-surface-300 px-2.5 py-1.5 text-xs resize-none focus:border-primary-500 focus:ring-1 focus:ring-primary-100 outline-none"
          rows="2"
        />
      </div>

      <!-- Spoke With Notes (just notes, no verification) -->
      <div
        v-if="voe.outcome.value === 'spoke_with'"
        class="px-4 py-3 border-b border-surface-200 space-y-2"
      >
        <div class="text-xs font-semibold text-primary-700 uppercase tracking-wider">
          Spoke With Details
        </div>
        <div class="text-[10px] text-primary-500 italic">Spoke with someone but did not complete verification. Add notes below.</div>
        <textarea
          v-model="voe.notes.value"
          placeholder="Who did you speak with? What was discussed? Any follow-up needed?"
          class="w-full rounded-lg border border-surface-300 px-2.5 py-1.5 text-xs resize-none focus:border-primary-500 focus:ring-1 focus:ring-primary-100 outline-none"
          rows="3"
        />
      </div>

      <!-- ═══ VERIFICATION CHECKLIST (Only shown when contact was made) ═══ -->
      <div
        v-if="voe.outcome.value === 'validated' || voe.outcome.value === 'spoke_with'"
        class="px-4 py-3 border-b border-surface-200 space-y-3"
      >
        <div class="text-xs font-semibold text-primary-700 uppercase tracking-wider">
          Verification
        </div>

        <!-- Respondent -->
        <div class="grid grid-cols-2 gap-2">
          <div>
            <label class="block text-xs text-surface-500 mb-0.5">Spoke With *</label>
            <input
              v-model="voe.verification.value.respondentName"
              class="w-full rounded-lg border border-surface-300 px-2.5 py-1.5 text-xs focus:border-primary-500 focus:ring-1 focus:ring-primary-100 outline-none"
              placeholder="Name..."
            />
          </div>
          <div>
            <label class="block text-xs text-surface-500 mb-0.5">Title</label>
            <input
              v-model="voe.verification.value.respondentTitle"
              class="w-full rounded-lg border border-surface-300 px-2.5 py-1.5 text-xs focus:border-primary-500 focus:ring-1 focus:ring-primary-100 outline-none"
              placeholder="Title..."
            />
          </div>
        </div>

        <!-- Employment Dates -->
        <div class="rounded-lg border border-surface-200 bg-surface-50 p-2.5">
          <div class="flex items-center justify-between mb-1">
            <div>
              <span class="text-xs font-medium text-surface-700">Employment Dates</span>
              <div class="text-[10px] text-primary-500 italic">"Can you confirm they worked there from... to...?"</div>
            </div>
            <div class="flex gap-1">
              <button
                :class="[
                  'px-2 py-0.5 rounded text-xs font-medium transition-colors',
                  voe.verification.value.datesConfirmed === true
                    ? 'bg-green-100 text-green-700' : 'bg-white text-surface-500 hover:bg-surface-100 border border-surface-200'
                ]"
                @click="voe.verification.value.datesConfirmed = true"
              ><i class="pi pi-check text-[10px] mr-0.5" />Correct</button>
              <button
                :class="[
                  'px-2 py-0.5 rounded text-xs font-medium transition-colors',
                  voe.verification.value.datesConfirmed === false
                    ? 'bg-amber-100 text-amber-700' : 'bg-white text-surface-500 hover:bg-surface-100 border border-surface-200'
                ]"
                @click="voe.verification.value.datesConfirmed = false"
              ><i class="pi pi-pencil text-[10px] mr-0.5" />Edit</button>
              <button
                :class="[
                  'px-2 py-0.5 rounded text-xs font-medium transition-colors',
                  voe.verification.value.datesConfirmed === 'unable'
                    ? 'bg-surface-300 text-surface-700' : 'bg-white text-surface-400 hover:bg-surface-100 border border-surface-200'
                ]"
                @click="voe.verification.value.datesConfirmed = 'unable' as any"
              >N/A</button>
            </div>
          </div>
          <!-- Display current values -->
          <div v-if="voe.verification.value.datesConfirmed !== false" class="text-xs text-surface-600">
            {{ formatDate(voe.verification.value.employmentStart) }} — {{ formatDate(voe.verification.value.employmentEnd) }}
          </div>
          <!-- Editable inputs when "Edit" selected -->
          <div v-else class="grid grid-cols-2 gap-2 mt-1.5">
            <div>
              <label class="block text-[10px] text-surface-400 mb-0.5">Start</label>
              <input
                v-model="voe.verification.value.employmentStart"
                type="date"
                class="w-full rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs focus:border-primary-500 focus:ring-1 focus:ring-primary-100 outline-none"
              />
            </div>
            <div>
              <label class="block text-[10px] text-surface-400 mb-0.5">End</label>
              <input
                v-model="voe.verification.value.employmentEnd"
                type="date"
                class="w-full rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs focus:border-primary-500 focus:ring-1 focus:ring-primary-100 outline-none"
              />
            </div>
          </div>
        </div>

        <!-- Position Held -->
        <div class="rounded-lg border border-surface-200 bg-surface-50 p-2.5">
          <div class="flex items-center justify-between mb-1">
            <div>
              <span class="text-xs font-medium text-surface-700">Position Held</span>
              <div class="text-[10px] text-primary-500 italic">"What position did they hold?"</div>
            </div>
            <div class="flex gap-1">
              <button
                :class="[
                  'px-2 py-0.5 rounded text-xs font-medium transition-colors',
                  voe.verification.value.positionConfirmed === true
                    ? 'bg-green-100 text-green-700' : 'bg-white text-surface-500 hover:bg-surface-100 border border-surface-200'
                ]"
                @click="voe.verification.value.positionConfirmed = true"
              ><i class="pi pi-check text-[10px] mr-0.5" />Correct</button>
              <button
                :class="[
                  'px-2 py-0.5 rounded text-xs font-medium transition-colors',
                  voe.verification.value.positionConfirmed === false
                    ? 'bg-amber-100 text-amber-700' : 'bg-white text-surface-500 hover:bg-surface-100 border border-surface-200'
                ]"
                @click="voe.verification.value.positionConfirmed = false"
              ><i class="pi pi-pencil text-[10px] mr-0.5" />Edit</button>
              <button
                :class="[
                  'px-2 py-0.5 rounded text-xs font-medium transition-colors',
                  voe.verification.value.positionConfirmed === 'unable'
                    ? 'bg-surface-300 text-surface-700' : 'bg-white text-surface-400 hover:bg-surface-100 border border-surface-200'
                ]"
                @click="voe.verification.value.positionConfirmed = 'unable' as any"
              >N/A</button>
            </div>
          </div>
          <div v-if="voe.verification.value.positionConfirmed !== false" class="text-xs text-surface-600">
            {{ voe.verification.value.positionHeld || 'Not specified' }}
          </div>
          <input
            v-else
            v-model="voe.verification.value.positionHeld"
            class="w-full rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs focus:border-primary-500 focus:ring-1 focus:ring-primary-100 outline-none mt-1"
            placeholder="Position held..."
          />
        </div>

        <!-- Accidents -->
        <div class="rounded-lg border border-surface-200 bg-surface-50 p-2.5">
          <div class="flex items-center justify-between">
            <div>
              <span class="text-xs font-medium text-surface-700">Accidents</span>
              <div class="text-[10px] text-primary-500 italic">"Were there any accidents during their employment?"</div>
            </div>
            <div class="flex gap-1">
              <button
                :class="[
                  'px-2 py-0.5 rounded text-xs font-medium transition-colors',
                  voe.verification.value.hasAccidents === false
                    ? 'bg-green-100 text-green-700' : 'bg-white text-surface-500 hover:bg-surface-100 border border-surface-200'
                ]"
                @click="voe.verification.value.hasAccidents = false; voe.verification.value.accidents = []"
              >None</button>
              <button
                :class="[
                  'px-2 py-0.5 rounded text-xs font-medium transition-colors',
                  voe.verification.value.hasAccidents === true
                    ? 'bg-red-100 text-red-700' : 'bg-white text-surface-500 hover:bg-surface-100 border border-surface-200'
                ]"
                @click="voe.verification.value.hasAccidents = true; if (voe.verification.value.accidents.length === 0) voe.verification.value.accidents.push('')"
              ><i class="pi pi-plus text-[10px] mr-0.5" />Add</button>
              <button
                :class="['px-2 py-0.5 rounded text-xs font-medium transition-colors', voe.verification.value.hasAccidents === ('unable' as any) ? 'bg-surface-300 text-surface-700' : 'bg-white text-surface-400 hover:bg-surface-100 border border-surface-200']"
                @click="voe.verification.value.hasAccidents = 'unable' as any; voe.verification.value.accidents = []"
              >N/A</button>
            </div>
          </div>
          <div v-if="voe.verification.value.hasAccidents" class="mt-2 space-y-1">
            <div
              v-for="(_, idx) in voe.verification.value.accidents"
              :key="idx"
              class="flex gap-1 items-center"
            >
              <input
                v-model="voe.verification.value.accidents[idx]"
                class="flex-1 rounded-lg border border-surface-300 bg-white px-2.5 py-1.5 text-xs focus:border-primary-500 focus:ring-1 focus:ring-primary-100 outline-none"
                :placeholder="`Accident ${idx + 1} details...`"
              />
              <button
                v-if="voe.verification.value.accidents.length > 1"
                class="text-surface-400 hover:text-red-500 p-1"
                @click="voe.verification.value.accidents.splice(idx, 1)"
              >
                <i class="pi pi-times text-[10px]" />
              </button>
            </div>
            <button
              class="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
              @click="voe.verification.value.accidents.push('')"
            >
              <i class="pi pi-plus text-[10px]" /> Add another
            </button>
          </div>
        </div>

        <!-- Violations -->
        <div class="rounded-lg border border-surface-200 bg-surface-50 p-2.5">
          <div class="flex items-center justify-between">
            <div>
              <span class="text-xs font-medium text-surface-700">Violations</span>
              <div class="text-[10px] text-primary-500 italic">"Were there any moving violations or safety issues?"</div>
            </div>
            <div class="flex gap-1">
              <button
                :class="[
                  'px-2 py-0.5 rounded text-xs font-medium transition-colors',
                  voe.verification.value.hasViolations === false
                    ? 'bg-green-100 text-green-700' : 'bg-white text-surface-500 hover:bg-surface-100 border border-surface-200'
                ]"
                @click="voe.verification.value.hasViolations = false; voe.verification.value.violations = []"
              >None</button>
              <button
                :class="[
                  'px-2 py-0.5 rounded text-xs font-medium transition-colors',
                  voe.verification.value.hasViolations === true
                    ? 'bg-red-100 text-red-700' : 'bg-white text-surface-500 hover:bg-surface-100 border border-surface-200'
                ]"
                @click="voe.verification.value.hasViolations = true; if (voe.verification.value.violations.length === 0) voe.verification.value.violations.push('')"
              ><i class="pi pi-plus text-[10px] mr-0.5" />Add</button>
              <button
                :class="['px-2 py-0.5 rounded text-xs font-medium transition-colors', voe.verification.value.hasViolations === ('unable' as any) ? 'bg-surface-300 text-surface-700' : 'bg-white text-surface-400 hover:bg-surface-100 border border-surface-200']"
                @click="voe.verification.value.hasViolations = 'unable' as any; voe.verification.value.violations = []"
              >N/A</button>
            </div>
          </div>
          <div v-if="voe.verification.value.hasViolations" class="mt-2 space-y-1">
            <div
              v-for="(_, idx) in voe.verification.value.violations"
              :key="idx"
              class="flex gap-1 items-center"
            >
              <input
                v-model="voe.verification.value.violations[idx]"
                class="flex-1 rounded-lg border border-surface-300 bg-white px-2.5 py-1.5 text-xs focus:border-primary-500 focus:ring-1 focus:ring-primary-100 outline-none"
                :placeholder="`Violation ${idx + 1} details...`"
              />
              <button
                v-if="voe.verification.value.violations.length > 1"
                class="text-surface-400 hover:text-red-500 p-1"
                @click="voe.verification.value.violations.splice(idx, 1)"
              >
                <i class="pi pi-times text-[10px]" />
              </button>
            </div>
            <button
              class="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
              @click="voe.verification.value.violations.push('')"
            >
              <i class="pi pi-plus text-[10px]" /> Add another
            </button>
          </div>
        </div>

        <!-- Reason for Leaving -->
        <div class="rounded-lg border border-surface-200 bg-surface-50 p-2.5">
          <div class="flex items-center justify-between mb-1">
            <div>
              <span class="text-xs font-medium text-surface-700">Reason for Leaving</span>
              <div class="text-[10px] text-primary-500 italic">"What was their reason for leaving?"</div>
            </div>
          </div>
          <input
            v-model="voe.verification.value.reasonForLeaving"
            class="w-full rounded-lg border border-surface-300 bg-white px-2.5 py-1.5 text-xs focus:border-primary-500 focus:ring-1 focus:ring-primary-100 outline-none"
            placeholder="Resigned, terminated, laid off..."
          />
        </div>

        <!-- Eligible for Rehire -->
        <div class="rounded-lg border border-surface-200 bg-surface-50 p-2.5">
          <div class="flex items-center justify-between">
            <div>
              <span class="text-xs font-medium text-surface-700">Eligible for Rehire</span>
              <div class="text-[10px] text-primary-500 italic">"Would they be eligible for rehire?"</div>
            </div>
            <div class="flex gap-1">
              <button
                :class="[
                  'px-2 py-0.5 rounded text-xs font-medium transition-colors',
                  voe.verification.value.eligibleForRehire === true
                    ? 'bg-green-100 text-green-700' : 'bg-white text-surface-500 hover:bg-surface-100 border border-surface-200'
                ]"
                @click="voe.verification.value.eligibleForRehire = true"
              >Yes</button>
              <button
                :class="[
                  'px-2 py-0.5 rounded text-xs font-medium transition-colors',
                  voe.verification.value.eligibleForRehire === false
                    ? 'bg-red-100 text-red-700' : 'bg-white text-surface-500 hover:bg-surface-100 border border-surface-200'
                ]"
                @click="voe.verification.value.eligibleForRehire = false"
              >No</button>
              <button
                :class="['px-2 py-0.5 rounded text-xs font-medium transition-colors', voe.verification.value.eligibleForRehire === ('unable' as any) ? 'bg-surface-300 text-surface-700' : 'bg-white text-surface-400 hover:bg-surface-100 border border-surface-200']"
                @click="voe.verification.value.eligibleForRehire = 'unable' as any"
              >N/A</button>
            </div>
          </div>
        </div>

        <!-- Subject to FMCSA -->
        <div class="rounded-lg border border-surface-200 bg-surface-50 p-2.5">
          <div class="flex items-center justify-between">
            <div>
              <span class="text-xs font-medium text-surface-700">Subject to FMCSA</span>
              <div class="text-[10px] text-primary-500 italic">"Were they subject to federal motor carrier safety regulations?"</div>
            </div>
            <div class="flex gap-1">
              <button
                :class="[
                  'px-2 py-0.5 rounded text-xs font-medium transition-colors',
                  voe.verification.value.subjectToFmcsa === true
                    ? 'bg-blue-100 text-blue-700' : 'bg-white text-surface-500 hover:bg-surface-100 border border-surface-200'
                ]"
                @click="voe.verification.value.subjectToFmcsa = true"
              >Yes</button>
              <button
                :class="[
                  'px-2 py-0.5 rounded text-xs font-medium transition-colors',
                  voe.verification.value.subjectToFmcsa === false
                    ? 'bg-surface-200 text-surface-700' : 'bg-white text-surface-500 hover:bg-surface-100 border border-surface-200'
                ]"
                @click="voe.verification.value.subjectToFmcsa = false"
              >No</button>
              <button
                :class="['px-2 py-0.5 rounded text-xs font-medium transition-colors', voe.verification.value.subjectToFmcsa === ('unable' as any) ? 'bg-surface-300 text-surface-700' : 'bg-white text-surface-400 hover:bg-surface-100 border border-surface-200']"
                @click="voe.verification.value.subjectToFmcsa = 'unable' as any"
              >N/A</button>
            </div>
          </div>
        </div>

        <!-- DOT Drug & Alcohol section (only if FMCSA = Yes) -->
        <template v-if="voe.verification.value.subjectToFmcsa === true">
          <div class="text-xs font-semibold text-primary-700 uppercase tracking-wider mt-1">
            DOT Drug &amp; Alcohol
          </div>

          <!-- Subject to D&A Testing -->
          <div class="rounded-lg border border-surface-200 bg-surface-50 p-2.5">
            <div class="flex items-center justify-between">
              <div>
                <span class="text-xs font-medium text-surface-700">D&amp;A Testing Required</span>
                <div class="text-[10px] text-primary-500 italic">"Were they subject to DOT drug and alcohol testing?"</div>
              </div>
              <div class="flex gap-1">
                <button
                  :class="['px-2 py-0.5 rounded text-xs font-medium transition-colors', voe.verification.value.subjectToDrugAlcoholTesting === true ? 'bg-blue-100 text-blue-700' : 'bg-white text-surface-500 hover:bg-surface-100 border border-surface-200']"
                  @click="voe.verification.value.subjectToDrugAlcoholTesting = true"
                >Yes</button>
                <button
                  :class="['px-2 py-0.5 rounded text-xs font-medium transition-colors', voe.verification.value.subjectToDrugAlcoholTesting === false ? 'bg-surface-200 text-surface-700' : 'bg-white text-surface-500 hover:bg-surface-100 border border-surface-200']"
                  @click="voe.verification.value.subjectToDrugAlcoholTesting = false"
                >No</button>
                <button
                  :class="['px-2 py-0.5 rounded text-xs font-medium transition-colors', voe.verification.value.subjectToDrugAlcoholTesting === ('unable' as any) ? 'bg-surface-300 text-surface-700' : 'bg-white text-surface-400 hover:bg-surface-100 border border-surface-200']"
                  @click="voe.verification.value.subjectToDrugAlcoholTesting = 'unable' as any"
                >N/A</button>
              </div>
            </div>
          </div>

          <!-- D&A Violations -->
          <div v-if="voe.verification.value.subjectToDrugAlcoholTesting === true" class="rounded-lg border border-surface-200 bg-surface-50 p-2.5">
            <div class="flex items-center justify-between">
              <div>
                <span class="text-xs font-medium text-surface-700">D&amp;A Violations</span>
                <div class="text-[10px] text-primary-500 italic">"Were there any drug or alcohol violations, positive tests, or refusals?"</div>
              </div>
              <div class="flex gap-1">
                <button
                  :class="['px-2 py-0.5 rounded text-xs font-medium transition-colors', voe.verification.value.hasDrugAlcoholViolation === false ? 'bg-green-100 text-green-700' : 'bg-white text-surface-500 hover:bg-surface-100 border border-surface-200']"
                  @click="voe.verification.value.hasDrugAlcoholViolation = false"
                >None</button>
                <button
                  :class="['px-2 py-0.5 rounded text-xs font-medium transition-colors', voe.verification.value.hasDrugAlcoholViolation === true ? 'bg-red-100 text-red-700' : 'bg-white text-surface-500 hover:bg-surface-100 border border-surface-200']"
                  @click="voe.verification.value.hasDrugAlcoholViolation = true"
                >Yes</button>
                <button
                  :class="['px-2 py-0.5 rounded text-xs font-medium transition-colors', voe.verification.value.hasDrugAlcoholViolation === ('unable' as any) ? 'bg-surface-300 text-surface-700' : 'bg-white text-surface-400 hover:bg-surface-100 border border-surface-200']"
                  @click="voe.verification.value.hasDrugAlcoholViolation = 'unable' as any"
                >N/A</button>
              </div>
            </div>
            <div v-if="voe.verification.value.hasDrugAlcoholViolation" class="mt-2">
              <input
                v-model="voe.verification.value.drugAlcoholViolationDetails"
                class="w-full rounded-lg border border-surface-300 bg-white px-2.5 py-1.5 text-xs focus:border-primary-500 focus:ring-1 focus:ring-primary-100 outline-none"
                placeholder="Violation details..."
              />
            </div>
          </div>

          <!-- SAP Program -->
          <div v-if="voe.verification.value.hasDrugAlcoholViolation === true" class="rounded-lg border border-surface-200 bg-surface-50 p-2.5">
            <div class="flex items-center justify-between">
              <div>
                <span class="text-xs font-medium text-surface-700">Completed SAP Program</span>
                <div class="text-[10px] text-primary-500 italic">"Did they complete a Substance Abuse Professional program?"</div>
              </div>
              <div class="flex gap-1">
                <button
                  :class="['px-2 py-0.5 rounded text-xs font-medium transition-colors', voe.verification.value.completedSapProgram === true ? 'bg-green-100 text-green-700' : 'bg-white text-surface-500 hover:bg-surface-100 border border-surface-200']"
                  @click="voe.verification.value.completedSapProgram = true"
                >Yes</button>
                <button
                  :class="['px-2 py-0.5 rounded text-xs font-medium transition-colors', voe.verification.value.completedSapProgram === false ? 'bg-red-100 text-red-700' : 'bg-white text-surface-500 hover:bg-surface-100 border border-surface-200']"
                  @click="voe.verification.value.completedSapProgram = false"
                >No</button>
                <button
                  :class="['px-2 py-0.5 rounded text-xs font-medium transition-colors', voe.verification.value.completedSapProgram === ('unable' as any) ? 'bg-surface-300 text-surface-700' : 'bg-white text-surface-400 hover:bg-surface-100 border border-surface-200']"
                  @click="voe.verification.value.completedSapProgram = 'unable' as any"
                >N/A</button>
              </div>
            </div>
          </div>

          <!-- Disqualified -->
          <div class="rounded-lg border border-surface-200 bg-surface-50 p-2.5">
            <div class="flex items-center justify-between">
              <div>
                <span class="text-xs font-medium text-surface-700">Disqualified (Section 391.15)</span>
                <div class="text-[10px] text-primary-500 italic">"Were they ever suspended or prohibited from driving a CMV?"</div>
              </div>
              <div class="flex gap-1">
                <button
                  :class="['px-2 py-0.5 rounded text-xs font-medium transition-colors', voe.verification.value.wasDisqualified === false ? 'bg-green-100 text-green-700' : 'bg-white text-surface-500 hover:bg-surface-100 border border-surface-200']"
                  @click="voe.verification.value.wasDisqualified = false"
                >No</button>
                <button
                  :class="['px-2 py-0.5 rounded text-xs font-medium transition-colors', voe.verification.value.wasDisqualified === true ? 'bg-red-100 text-red-700' : 'bg-white text-surface-500 hover:bg-surface-100 border border-surface-200']"
                  @click="voe.verification.value.wasDisqualified = true"
                >Yes</button>
                <button
                  :class="['px-2 py-0.5 rounded text-xs font-medium transition-colors', voe.verification.value.wasDisqualified === ('unable' as any) ? 'bg-surface-300 text-surface-700' : 'bg-white text-surface-400 hover:bg-surface-100 border border-surface-200']"
                  @click="voe.verification.value.wasDisqualified = 'unable' as any"
                >N/A</button>
              </div>
            </div>
          </div>

          <!-- Work Record Notes -->
          <div class="rounded-lg border border-surface-200 bg-surface-50 p-2.5">
            <div class="flex items-center justify-between mb-1">
              <div>
                <span class="text-xs font-medium text-surface-700">Work Record Notes</span>
                <div class="text-[10px] text-primary-500 italic">"Any performance concerns or disciplinary actions?"</div>
              </div>
            </div>
            <input
              v-model="voe.verification.value.workRecordNotes"
              class="w-full rounded-lg border border-surface-300 bg-white px-2.5 py-1.5 text-xs focus:border-primary-500 focus:ring-1 focus:ring-primary-100 outline-none"
              placeholder="Performance issues, disciplinary actions..."
            />
          </div>
        </template>
      </div>

      <!-- General Notes (shown for all outcomes except spoke_with which has its own) -->
      <div v-if="voe.outcome.value !== 'spoke_with'" class="px-4 py-3">
        <div class="text-xs font-semibold text-primary-700 uppercase tracking-wider mb-1.5">
          Notes
        </div>
        <textarea
          v-model="voe.notes.value"
          placeholder="Call notes..."
          class="w-full rounded-lg border border-surface-300 px-2.5 py-1.5 text-xs resize-none focus:border-primary-500 focus:ring-1 focus:ring-primary-100 outline-none"
          rows="2"
        />
      </div>
    </div>

    <!-- ═══ BOTTOM BAR — Dial / Call Controls / Quick Actions / Save ═══ -->
    <div class="shrink-0 border-t border-primary-200 bg-primary-50 px-4 py-3 space-y-2.5">

      <!-- Pre-call: Dial buttons + quick actions -->
      <div v-if="voe.mode.value === 'call' && !phone.isOnCall && !voe.isDeskPhoneActive.value" class="space-y-2">
        <div class="flex gap-2">
          <button
            class="flex-1 flex items-center justify-center gap-2 rounded-xl bg-primary-700 py-2.5 text-sm text-white font-medium hover:bg-primary-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            :disabled="!voe.canDial.value"
            @click="voe.dial()"
          >
            <i class="pi pi-phone text-xs" />
            Softphone
          </button>
          <button
            class="flex-1 flex items-center justify-center gap-2 rounded-xl bg-primary-600 py-2.5 text-sm text-white font-medium hover:bg-primary-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            :disabled="!voe.canDialDeskPhone.value"
            @click="voe.dialDeskPhone()"
          >
            <i class="pi pi-desktop text-xs" />
            Desk Phone
          </button>
        </div>
        <div class="flex gap-2">
          <button
            class="flex-1 flex items-center justify-center gap-1.5 rounded-lg border border-primary-200 bg-white py-1.5 text-xs font-medium text-primary-700 hover:bg-primary-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            :disabled="!voe.canDropVoicemail.value"
            @click="voe.dropVoicemail()"
            v-tooltip.top="'Must be on active call'"
          >
            <i class="pi pi-volume-up text-[10px]" />
            Drop VM
          </button>
          <button
            class="flex-1 flex items-center justify-center gap-1.5 rounded-lg border border-primary-200 bg-white py-1.5 text-xs font-medium text-primary-700 hover:bg-primary-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            :disabled="!voe.canSendForm.value"
            @click="voe.sendFormLink()"
            v-tooltip.top="'Send verification form via email'"
          >
            <i class="pi pi-envelope text-[10px]" />
            Email Form
          </button>
        </div>
      </div>

      <!-- Desk phone ringing / in-progress status -->
      <div v-if="voe.isDeskPhoneActive.value" class="text-center py-1">
        <div class="text-sm font-medium text-primary-800">
          <i class="pi pi-desktop mr-1" />
          <span v-if="voe.deskPhoneStatus.value === 'ringing'">Ringing desk phone...</span>
          <span v-else>Connected via desk phone</span>
        </div>
        <div class="text-xs text-primary-600 mt-0.5">
          {{ voe.formattedPhone.value }}
        </div>
      </div>

      <!-- Active softphone call: mute, #, hangup + quick actions -->
      <div v-if="voe.mode.value === 'call' && phone.isOnCall" class="space-y-2">
        <div class="flex items-center justify-center gap-3">
          <button
            :class="[
              'rounded-full p-2.5 transition-colors shadow-sm',
              phone.isMuted ? 'bg-red-100 text-red-600 ring-1 ring-red-200' : 'bg-white text-primary-700 hover:bg-primary-100 ring-1 ring-primary-200'
            ]"
            @click="phone.toggleMute()"
            v-tooltip.top="phone.isMuted ? 'Unmute' : 'Mute'"
          >
            <i :class="phone.isMuted ? 'pi pi-microphone-slash' : 'pi pi-microphone'" class="text-sm" />
          </button>
          <button
            class="rounded-full p-2.5 bg-white text-primary-700 hover:bg-primary-100 ring-1 ring-primary-200 transition-colors shadow-sm"
            @click="phone.sendDigits('#')"
            v-tooltip.top="'Send #'"
          >
            <i class="pi pi-hashtag text-sm" />
          </button>
          <button
            class="rounded-full p-2.5 bg-white text-primary-700 hover:bg-primary-100 ring-1 ring-primary-200 transition-colors shadow-sm"
            :disabled="!voe.canDropVoicemail.value"
            :class="{ 'opacity-40 cursor-not-allowed': !voe.canDropVoicemail.value }"
            @click="voe.dropVoicemail()"
            v-tooltip.top="'Drop voicemail & hang up'"
          >
            <i class="pi pi-volume-up text-sm" />
          </button>
          <button
            class="rounded-full bg-red-600 p-2.5 text-white hover:bg-red-700 transition-colors shadow-sm"
            @click="phone.hangup()"
            v-tooltip.top="'End Call'"
          >
            <i class="pi pi-phone text-sm" style="transform: rotate(135deg)" />
          </button>
        </div>
      </div>

      <!-- Active desk phone call: hangup only -->
      <div v-else-if="voe.mode.value === 'call' && voe.isDeskPhoneActive.value" class="flex justify-center">
        <button
          class="rounded-full bg-red-600 p-2.5 text-white hover:bg-red-700 transition-colors shadow-sm"
          @click="voe.hangupDeskPhone()"
          v-tooltip.top="'End Call'"
        >
          <i class="pi pi-phone text-sm" style="transform: rotate(135deg)" />
        </button>
      </div>

      <!-- Save / Cancel -->
      <div class="flex gap-2">
        <button
          class="flex-1 rounded-xl border border-primary-200 bg-white py-2 text-sm font-medium text-primary-700 hover:bg-primary-50 transition-colors disabled:opacity-40"
          :disabled="voe.isSaving.value"
          @click="voe.exitVOEMode()"
        >
          Cancel
        </button>
        <button
          class="flex-1 rounded-xl bg-primary-700 py-2 text-sm font-medium text-white hover:bg-primary-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          :disabled="!voe.canSave.value || voe.isSaving.value"
          @click="voe.saveAndClose()"
        >
          {{ voe.isSaving.value ? 'Saving...' : 'Save' }}
        </button>
      </div>
    </div>
  </div>
</template>
